import type { ComponentType } from 'react';
import CreateComponentIcon from '@material-ui/icons/Create';
import RegisterComponentIcon from '@material-ui/icons/AddCircleOutline';
import CategoryIcon from '@material-ui/icons/Category';
import PlaylistPlayIcon from '@material-ui/icons/PlaylistPlay';
import SettingsIcon from '@material-ui/icons/Settings';
import MapIcon from '@material-ui/icons/Map';
import TimelineIcon from '@material-ui/icons/Timeline';
import BuildIcon from '@material-ui/icons/Build';
import LiveHelpIcon from '@material-ui/icons/LiveHelp';
import EmojiObjectsOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import WarningIcon from '@material-ui/icons/Warning';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import GitHubIcon from '@material-ui/icons/GitHub';
import DescriptionIcon from '@material-ui/icons/Description';
import { GraphiQLIcon } from '@backstage-community/plugin-graphiql';
import WorkIcon from '@material-ui/icons/Work';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';

export const STORAGE_BUCKET = 'sidebar-preferences';
export const FAVORITES_KEY = 'favorites';
export const LANDING_PAGE_KEY = 'landingPage';
export const HIDDEN_ITEMS_KEY = 'hiddenItems';
export const MENU_ORDER_KEY = 'menuOrder';
export const PREFERENCES_VERSION_KEY = 'preferencesVersion';

// Default menu order for the middle section.
// APIs and Docs are intentionally absent: both are now views of /catalog,
// selectable through the Kind filter on that page.
export const DEFAULT_MENU_ORDER = [
  '/my-work',
  '/tools-resources',
  '/catalog',
  '/adr',
  '/graphiql',
  '/catalog-import',
  '/create',
  '/mga',
  '/my-ai-tools',
  '/playlist',
  '/tech-radar',
  '/golden-paths',
  '/github',
  '/qeta',
  '/infrawallet',
  '/devtools',
];

/**
 * Default landing page path when no user preference is set.
 * Used by RootRedirect.
 */
export const DEFAULT_LANDING_PAGE = '/dashboard';

/**
 * Timeout in milliseconds for waiting on storage to resolve before
 * falling back to the default landing page. This prevents the app
 * from blocking indefinitely if storage is slow or unavailable.
 * Used by RootRedirect.
 */
export const STORAGE_LOAD_TIMEOUT_MS = 1000;

// Default middle-section menu items to hide on first load (all paths normalized with leading slash)
// In the middle menu section, only /dashboard (Home) is visible by default — users add other items from homepage feature cards
export const DEFAULT_HIDDEN_ITEMS = [
  '/catalog',
  '/adr',
  '/graphiql',
  '/catalog-import',
  '/create',
  '/mga',
  '/playlist',
  '/tech-radar',
  '/golden-paths',
  '/qeta',
  '/infrawallet',
  '/devtools',
  '/my-work',
  '/datadog-analytics',
  '/tools-resources',
];

// Map of route paths to their icons and text
// All paths are normalized with leading slashes for consistency
export const SIDEBAR_ITEMS_CONFIG: Record<
  string,
  { icon: ComponentType<any>; text: string }
> = {
  '/catalog': { icon: CategoryIcon, text: 'Catalog' },
  '/adr': { icon: DescriptionIcon, text: 'ADRs' },
  '/graphiql': { icon: GraphiQLIcon, text: 'GraphiQL' },
  '/catalog-import': { icon: RegisterComponentIcon, text: 'Register' },
  '/create': { icon: CreateComponentIcon, text: 'Create' },
  '/mga': { icon: EmojiObjectsOutlinedIcon, text: 'AI Hub' },
  '/my-ai-tools': { icon: EmojiObjectsOutlinedIcon, text: 'My AI Tools' },
  '/playlist': { icon: PlaylistPlayIcon, text: 'Playlists' },
  '/tech-radar': { icon: MapIcon, text: 'Tech Radar' },
  '/golden-paths': { icon: TimelineIcon, text: 'Golden Paths' },
  '/github': { icon: GitHubIcon, text: 'GitHub' },
  '/qeta': { icon: LiveHelpIcon, text: 'Q&A' },
  '/infrawallet': { icon: AccountBalanceWalletIcon, text: 'Infra Wallet' },
  '/settings': { icon: SettingsIcon, text: 'Settings' },
  '/devtools': { icon: BuildIcon, text: 'Dev Tools' },
  '/catalog-unprocessed-entities': {
    icon: WarningIcon,
    text: 'Unprocessed Entities',
  },
  // Permission-gated admin item — rendered only via DatadogAnalyticsSidebarItem
  // (not in DEFAULT_MENU_ORDER). Keep this entry so hide/restore via "Add Items" works.
  '/datadog-analytics': { icon: ShowChartIcon, text: 'Analytics' },
  '/my-work': { icon: WorkIcon, text: 'My Work' },
  '/tools-resources': { icon: BuildOutlinedIcon, text: 'Resources and Tools' },
};

/**
 * Sidebar paths that no longer have an entry in {@link SIDEBAR_ITEMS_CONFIG}
 * because they became Kind filters on /catalog.
 */
export const RETIRED_SIDEBAR_PATHS = ['/docs', '/api-docs'];

export const CATALOG_SIDEBAR_PATH = '/catalog';

/**
 * Equivalent /catalog deep links for the retired paths, used to keep a stored
 * landing page pointing at the same view.
 *
 * The query parameters are the ones `EntityListProvider` serializes its kind
 * and techdocs filters into, which `useUnifiedKindFilter` reads back to
 * restore the selection.
 */
const RETIRED_SIDEBAR_PATH_REPLACEMENTS: Record<string, string> = {
  // `filters[user]=owned` preserves the old Docs home landing scope.
  '/docs': `${CATALOG_SIDEBAR_PATH}?${new URLSearchParams({
    'filters[techdocs]': 'documentation',
    'filters[user]': 'owned',
  })}`,
  '/api-docs': `${CATALOG_SIDEBAR_PATH}?${new URLSearchParams({
    'filters[kind]': 'api',
    // Matches CustomCatalogPage's legacy /api-docs entry point, which seeded
    // UserListPicker with `all` rather than the catalog default of `owned`.
    'filters[user]': 'all',
  })}`,
};

export type SidebarPathPreferences = {
  version?: number;
  menuOrder?: string[];
  hiddenItems?: string[];
  landingPage?: string;
  favorites?: string[];
};

/**
 * Current revision of the stored sidebar preferences. Bumped whenever a
 * migration is added to {@link migrateRetiredSidebarPaths}.
 */
export const SIDEBAR_PREFERENCES_VERSION = 2;

/**
 * One-time migration for the sidebar entries retired by the catalog
 * consolidation.
 *
 * Stored preferences are restored verbatim, so without this a user who had
 * Docs or API Docs visible while Catalog was hidden would come out of the
 * upgrade with no catalog navigation at all: the retired paths have no config
 * left to render, and Catalog stays in their hidden list. Such users get
 * Catalog unhidden, and every user has the retired paths stripped from their
 * persisted order and hidden list.
 *
 * A landing page pointing at a retired path still resolves — both routes
 * render the unified page — but no sidebar item carries that path any more, so
 * the preference becomes invisible and impossible to clear. It is repointed at
 * the equivalent /catalog deep link, which lands on the same view and is
 * manageable from the Catalog item again.
 *
 * Favorites that still point at retired paths are rewritten to Catalog: the
 * Favorites section drops any path without a SIDEBAR_ITEMS_CONFIG entry, so
 * those entries would otherwise vanish while remaining stuck in storage.
 *
 * Gated on a stored version rather than on the retired paths still being
 * present. Those paths used to be in `DEFAULT_HIDDEN_ITEMS`, so a user who
 * revealed Docs is represented by their *absence* from the stored hidden list —
 * exactly the population this migration exists for. `menuOrder` is no better a
 * marker, because `DraggableMenuSection` appends missing defaults at render
 * time instead of persisting them.
 *
 * @param preferences - Values currently in storage; omit a key that is unset.
 * @returns Only the keys that need to be written back, including the version
 *          stamp. Empty once the stored preferences are already current.
 */
export const migrateRetiredSidebarPaths = ({
  version,
  menuOrder,
  hiddenItems,
  landingPage,
  favorites,
}: SidebarPathPreferences): SidebarPathPreferences => {
  if ((version ?? 0) >= SIDEBAR_PREFERENCES_VERSION) {
    return {};
  }

  const isRetired = (path: string) => RETIRED_SIDEBAR_PATHS.includes(path);
  const migration: SidebarPathPreferences = {
    version: SIDEBAR_PREFERENCES_VERSION,
  };

  if (menuOrder?.some(isRetired)) {
    migration.menuOrder = menuOrder.filter(path => !isRetired(path));
  }

  // Only a stored hidden list can say anything about what this user saw. When
  // it is unset the pre-change defaults applied, and those hid both retired
  // paths, so there is no visible entry to preserve and the post-change
  // defaults are written by the normal initialization path instead.
  if (hiddenItems) {
    const wasRetiredPathVisible = RETIRED_SIDEBAR_PATHS.some(
      path => !hiddenItems.includes(path),
    );
    const nextHiddenItems = hiddenItems.filter(
      path =>
        !isRetired(path) &&
        !(wasRetiredPathVisible && path === CATALOG_SIDEBAR_PATH),
    );
    if (nextHiddenItems.length !== hiddenItems.length) {
      migration.hiddenItems = nextHiddenItems;
    }
  }

  if (landingPage && isRetired(landingPage)) {
    migration.landingPage = RETIRED_SIDEBAR_PATH_REPLACEMENTS[landingPage];
  }

  if (favorites?.some(isRetired)) {
    // Map each retired favorite to Catalog, then dedupe so /docs+/api-docs
    // become a single Catalog star rather than two identical entries.
    migration.favorites = [
      ...new Set(
        favorites.map(path =>
          isRetired(path) ? CATALOG_SIDEBAR_PATH : path,
        ),
      ),
    ];
  }

  return migration;
};

// Helper function to normalize paths (ensure leading slash)
export const normalizePath = (path: string): string => {
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
};

/**
 * Validates that a path is a safe internal route.
 * Returns true if the path is valid for internal navigation.
 * Used to prevent open redirect attacks.
 *
 * @param path - The path to validate
 * @returns true if the path is a valid internal path
 */
export const isValidInternalPath = (path: string): boolean => {
  // Must be a non-empty string starting with / but not //
  // Paths starting with // could be protocol-relative URLs (e.g., //evil.com)
  return Boolean(path && path.startsWith('/') && !path.startsWith('//'));
};

/**
 * Gets the target landing page, falling back to default if invalid.
 * Ensures the returned path is always a valid internal path.
 *
 * @param landingPage - The user's preferred landing page
 * @returns A valid internal path for navigation
 */
export const getValidLandingPage = (
  landingPage: string | undefined,
): string => {
  if (!landingPage || landingPage === '/') {
    return DEFAULT_LANDING_PAGE;
  }
  const normalizedPath = normalizePath(landingPage);
  return isValidInternalPath(normalizedPath)
    ? normalizedPath
    : DEFAULT_LANDING_PAGE;
};
